import type { ComponentType } from 'react'
import clsx from 'clsx'
import { FilePdf, Document, AlertErrorCircle, XCircle } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'

/**
 * How a staged file sits against the living asset (icon + coloured copy). Each feature's
 * `dispositionDisplay` result structurally satisfies this; only the copy differs.
 */
export type MatterRecordFileDisposition = {
  text: string
  className: string
  Icon: ComponentType<{ className?: string }>
  /** Swaps the icon for a spinner while the disposition is still resolving. */
  loading?: boolean
}

type MatterRecordStagedFileCardProps = {
  fileName: string
  /**
   * Italic progress line shown while the upload pipeline is still running, e.g. "Uploading...".
   * Null or omitted once the file is idle. Takes precedence over the disposition line, and yields
   * to `errorMessage`.
   */
  inProgressLabel?: string | null
  disposition: MatterRecordFileDisposition | null
  duplicateOf?: string
  errorMessage?: string
  onRemove: () => void
  removeDisabled?: boolean
  onOpen?: () => void
}

/**
 * A staged document row on an Add Files surface (Figma "upload-card"): a file icon, the filename,
 * and the file's disposition against the living asset as the status line. Distinct from the
 * pending-upload file card, which renders upload/extraction progress.
 *
 * A file is staged the moment init-upload returns a user_file_id — before the storage transfer and
 * server processing have necessarily finished. While that pipeline is still running the row shows a
 * spinner plus `inProgressLabel`, so it reflects real progress instead of sitting static until the
 * server preview disposition lands.
 *
 * The row and the remove control are siblings: when `onOpen` is given the row becomes its own
 * `<button>`, so the remove button is never nested inside another interactive element.
 */
export function MatterRecordStagedFileCard({
  fileName,
  inProgressLabel,
  disposition,
  duplicateOf,
  errorMessage,
  onRemove,
  removeDisabled,
  onOpen,
}: MatterRecordStagedFileCardProps) {
  const FileIcon = fileName.toLowerCase().endsWith('.pdf') ? FilePdf : Document
  const Icon = disposition?.Icon
  const clickable = Boolean(onOpen)
  const inProgress = !errorMessage && Boolean(inProgressLabel)

  const rowContent = (
    <>
      <div className="w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px]">
        {inProgress ? <CircularProgressContinuousSized size={24} thickness={3} showTrack={true} /> : <FileIcon className="w-6 h-6 text-brand-neutral-800" />}
      </div>
      <div className="flex-1 flex flex-col gap-1 justify-center pr-12 min-w-0">
        <p className="text-[13px] font-medium text-brand-neutral-950 truncate">{fileName}</p>
        {errorMessage ? (
          <div className="flex items-center gap-1 text-red-800">
            <AlertErrorCircle className="w-3.5 h-3.5" />
            <p className="text-xs font-medium">{errorMessage}</p>
          </div>
        ) : inProgress ? (
          <p className="text-xs font-medium italic text-brand-neutral-600">{inProgressLabel}</p>
        ) : (
          disposition &&
          Icon && (
            <div className={clsx('flex items-center gap-1', disposition.className)}>
              {disposition.loading ? (
                <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center">
                  <CircularProgressContinuousSized size={12} thickness={4} />
                </span>
              ) : (
                <Icon className="w-3.5 h-3.5 shrink-0" />
              )}
              <p className="text-xs font-medium">{disposition.text}</p>
            </div>
          )
        )}
        {duplicateOf && <p className="text-xs font-medium text-yellow-700">Possible duplicate of {duplicateOf}</p>}
      </div>
    </>
  )
  const rowClassName = clsx('h-full w-full flex items-center gap-[10px] rounded-[5px] text-left', clickable && 'cursor-pointer')

  return (
    <div
      className={clsx(
        'h-[60px] flex items-center rounded-[5px] border border-brand-neutral-200 bg-white relative',
        clickable && 'hover:border-brand-neutral-300'
      )}
    >
      {clickable ? (
        <button type="button" onClick={onOpen} className={rowClassName}>
          {rowContent}
        </button>
      ) : (
        <div className={rowClassName}>{rowContent}</div>
      )}
      <IconButton
        icon={<XCircle />}
        variant="plain"
        color="neutral"
        size="md"
        onClick={onRemove}
        disabled={removeDisabled}
        wrapperClassName="absolute right-3 top-1/2 -translate-y-1/2"
        aria-label={`Remove ${fileName}`}
      />
    </div>
  )
}
